import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { GraduationCap, FileStack, Clock, CheckCircle2 } from "lucide-react";

function Stat({ label, value, testId, icon: Icon }) {
  return (
    <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="tech-label">{label}</p>
        {Icon && <Icon size={16} className="text-zinc-400"/>}
      </div>
      <p className="mt-3 font-display text-4xl font-black tracking-tighter">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="p-10 text-zinc-500">Loading…</div>;

  const statusData = Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k.replace("_", " "), count: v, key: k }));
  const trainingData = [
    { name: "With Training", count: stats.with_training },
    { name: "Without Training", count: stats.without_training },
  ];
  const colors = { pending: "#F59E0B", under_review: "#6366F1", approved: "#10B981", rejected: "#EF4444" };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8">
        <p className="tech-label">Overview</p>
        <h1 className="font-display text-4xl font-black tracking-tighter">Dashboard.</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Applications" value={stats.total} icon={FileStack} testId="stat-total"/>
        <Stat label="With Training" value={stats.with_training} icon={GraduationCap} testId="stat-with-training"/>
        <Stat label="Without Training" value={stats.without_training} icon={GraduationCap} testId="stat-without-training"/>
        <Stat label="Pending" value={stats.by_status?.pending ?? 0} icon={Clock} testId="stat-pending"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="border border-zinc-200 rounded-lg bg-white p-6">
          <p className="tech-label mb-4">Status Distribution</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false}/>
                <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 6, fontSize: 12 }}/>
                <Bar dataKey="count" radius={[4,4,0,0]}>
                  {statusData.map((e) => <Cell key={e.key} fill={colors[e.key] || "#09090B"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-zinc-200 rounded-lg bg-white p-6">
          <p className="tech-label mb-4">Training Split</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trainingData}>
                <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false}/>
                <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 6, fontSize: 12 }}/>
                <Bar dataKey="count" fill="#09090B" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <p className="tech-label">Recent Applications</p>
          <Link to="/admin/applications" className="text-xs font-semibold text-zinc-900 hover:underline">View all →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left">
            <tr>
              <th className="p-3 font-semibold text-zinc-600 text-xs uppercase tracking-wider">App No</th>
              <th className="p-3 font-semibold text-zinc-600 text-xs uppercase tracking-wider">Name</th>
              <th className="p-3 font-semibold text-zinc-600 text-xs uppercase tracking-wider">Training</th>
              <th className="p-3 font-semibold text-zinc-600 text-xs uppercase tracking-wider">Fee</th>
              <th className="p-3 font-semibold text-zinc-600 text-xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent?.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No applications yet</td></tr>
            ) : stats.recent?.map((r) => (
              <tr key={r.application_no} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors">
                <td className="p-3 font-mono text-xs">{r.application_no}</td>
                <td className="p-3">{r.full_name}</td>
                <td className="p-3 text-xs">{r.training_status === "with_training" ? "With" : "Without"}</td>
                <td className="p-3">₹{r.fee_amount?.toLocaleString("en-IN")}</td>
                <td className="p-3"><span className="text-xs font-bold uppercase tracking-wider bg-zinc-100 rounded px-2 py-0.5">{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
